import React, { useEffect, useRef, useState } from "react";
import { Loader2, Film } from "lucide-react";
import { clipPreviewUrl } from "@/lib/api";

/**
 * Looping silent video preview of an annotated clip MP4.
 *
 * Hosts the playback affordance for three different surfaces — Match
 * Library tile, Sequences row, and the Dashboard heatmap hover-card —
 * so we centralise the loading-spinner, error-fallback, and "lazy first
 * render" UX in one place. Pass `clipId` and we'll resolve the inline
 * MP4 stream URL ourselves.
 *
 * Behaviour notes:
 *  • The backend's `preview.mp4` endpoint takes 5-10 s on the FIRST hit
 *    per clip (lazy ffmpeg render). We show a spinner while the video
 *    metadata loads and gracefully fall back to a static placeholder if
 *    the response is a 404 (clip not tracked yet).
 *  • Autoplay-muted-loop is the only way Chromium will start playback
 *    without an explicit user gesture; we double-down with `playsInline`
 *    so Safari follows suit.
 *  • The component DOES NOT mount the video element until `enabled` is
 *    true — so opening a 100-row library page doesn't fetch 100 MP4s
 *    simultaneously. Hover toggles enabled=true on the parent.
 */
export default function HoverPreview({
  clipId,
  className = "",
  posterUrl = null,
  fallbackLabel = "no preview yet",
  enabled = true,
}) {
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
  }, [clipId]);

  if (!enabled) {
    // Lightweight placeholder — pure poster, no video element.
    return (
      <div
        data-testid={`clip-preview-placeholder-${clipId}`}
        className={`relative bg-black/40 ${className}`}
        style={posterUrl ? { backgroundImage: `url(${posterUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
      />
    );
  }

  if (error) {
    return (
      <div
        data-testid={`clip-preview-error-${clipId}`}
        className={`relative flex items-center justify-center bg-black/60 text-muted-foreground ${className}`}
      >
        <Film className="w-4 h-4 mr-1.5 opacity-60" />
        <span className="font-mono text-[10px] uppercase tracking-widest">{fallbackLabel}</span>
      </div>
    );
  }

  return (
    <div className={`relative bg-black ${className}`}>
      <video
        ref={videoRef}
        data-testid={`clip-preview-video-${clipId}`}
        src={clipPreviewUrl(clipId)}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onLoadedData={() => setLoading(false)}
        onError={(e) => {
          // MEDIA_ERR_ABORTED (1) fires when React StrictMode double-
          // mounts in dev OR when the parent unmounts mid-load — both
          // are recoverable, so we DON'T flip to the error fallback for
          // them. We only treat genuine network / decode failures (2, 3,
          // 4) as terminal. Without this guard the very first hover in
          // dev would show "render queued" forever.
          const code = e?.target?.error?.code;
          if (code && code !== 1) {
            setLoading(false);
            setError(true);
          }
        }}
        poster={posterUrl || undefined}
        className="block w-full h-full object-cover"
      />
      {loading && (
        <div
          data-testid={`clip-preview-loading-${clipId}`}
          className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm pointer-events-none"
        >
          <Loader2 className="w-4 h-4 text-pink-300 animate-spin" />
        </div>
      )}
    </div>
  );
}
